import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DestroyableComponent } from '../../../common/destroyable.component';
import { FeedInGridConnectionLevel } from '../../entities/enums/feedInGridConnectionLevel';
import { FeedInGridConnectionTransformation } from '../../entities/enums/feedInGridConnectionTransformation';
import { GridConnectionType } from '../../entities/enums/gridConnectionType';
import { Document } from '../../../jurisdiction/jurisdiction.domain';
import { FormatterData, InstallationFlowType } from '../../../constants/app.constants';
import { GridCompOrderProcess } from '../../entities/gridCompOrderProcess';
import { GridCompatibilityOrderProcessService } from '../../services/grid-compatibility-order-process.service';
import { Observable, forkJoin, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { GridCompatibilityProcessStatus } from '../../entities/enums/gridCompatibilityProcessStatus';
import { GridCompResultData } from '../../entities/gridCompResultData';
import { GridCompatibilityService } from '../../services/grid-compatibility.service';
import { GridApplicationDocumentTypes } from '../../../+grid-application-shared/entities/enums/grid-application-document-types.enum';
import { GridCompatibilityResultDocument } from '../../entities/gridCompatibilityResultDocument';
import { ConfirmationService } from 'primeng';
import { NetzTranslateService } from '../../../services/netz-translate.service';
import { Router } from '@angular/router';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ErrorService } from '../../../services/error/error.service';
import { GridApplicationProcessActionTypes } from '../../../+grid-application-shared/entities/enums/grid-application-process-action-types.enum';
import { DocumentAndInputName } from '../../../+shared/models/documentAndInputName';
import { ValidationsService } from 'src/app/services/validations.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { UserService } from 'src/app/services/user.service';
import { SkApplicationType } from 'src/app/domains/common.domain';
import { CopyApplicationDataService } from 'src/app/jurisdiction/copy-application-data/copy-application-data.service';

@Component({
  selector: 'app-grid-compatibility-assessment-result',
  templateUrl: './grid-compatibility-assessment-result.component.html',
  styleUrls: ['./grid-compatibility-assessment-result.component.scss'],
})
export class GridCompatibilityAssessmentResultComponent extends DestroyableComponent implements OnInit {
  @Input() applicationId: number;
  @Input() isModerator: boolean;

  @Output() resultFormChange = new EventEmitter<GridCompResultData>();
  @Output() executedProcessAction = new EventEmitter<GridApplicationProcessActionTypes>();

  enums = {
    level: FeedInGridConnectionLevel,
    transformation: FeedInGridConnectionTransformation,
    type: GridConnectionType,
    orderProcessStatus: GridCompatibilityProcessStatus,
    documentTypes: GridApplicationDocumentTypes,
  };

  dateFormat = FormatterData.date;
  orderProcess$: Observable<GridCompOrderProcess>;
  resultForm?: FormGroup;
  minDate: Date = new Date();
  isLoading = false;
  isGridAssessmentDocumentUploaded = false;
  isSitePlanDocumentUploaded = false;
  canCopyApplicationData = false;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private service: GridCompatibilityOrderProcessService,
    public gridCompatibilityService: GridCompatibilityService,
    private confirmationService: ConfirmationService,
    private translate: NetzTranslateService,
    private localStorageService: LocalStorageService,
    private errorService: ErrorService,
    private environmentService: EnvironmentService,
    private userService: UserService,
    private copyAppDataService: CopyApplicationDataService
  ) {
    super();
  }

  ngOnInit(): void {
    this.orderProcess$ = this.service.orderProcess$;

    this.preventLeak(this.orderProcess$)
      .pipe(
        filter(
          orderProcess =>
            orderProcess.status === GridCompatibilityProcessStatus.RequestSent ||
            orderProcess.status === GridCompatibilityProcessStatus.RequestUpdated ||
            orderProcess.status === GridCompatibilityProcessStatus.OpenIssue ||
            orderProcess.status === GridCompatibilityProcessStatus.FeedInPermissionIssued ||
            orderProcess.status === GridCompatibilityProcessStatus.FeedInPermissionExpired ||
            orderProcess.status === GridCompatibilityProcessStatus.ExtensionOfFeedInPermissionRequested
        ),
        map(orderProcess => {
          this.canCopyApplicationData = orderProcess.status === GridCompatibilityProcessStatus.FeedInPermissionIssued;
          return this.buildResultForm(orderProcess);
        })
      )
      .subscribe(form => {
        this.resultForm = form;
        this.isGridAssessmentDocumentUploaded = !!this.resultForm.controls.gridAssessmentDocument?.value.filename;
        this.isSitePlanDocumentUploaded = !!this.resultForm.controls.sitePlan?.value.filename;
      });

    this.service.loadOrderProcess(this.applicationId);
    this.minDate.setDate(this.minDate.getDate());
  }

  issuePermission(id: number, data: GridCompResultData, sitePlan: Document, gridAssessmentDocument: Document) {
    const sitePlanUpload$: Observable<number | null> =
      sitePlan.filename && sitePlan.document_id == null
        ? this.gridCompatibilityService.uploadDocuments([sitePlan], this.applicationId.toString())
        : of(null);

    const gridAssessmentDocumentUpload$: Observable<number | null> =
      gridAssessmentDocument.filename && gridAssessmentDocument.document_id == null
        ? this.gridCompatibilityService.uploadDocuments([gridAssessmentDocument], this.applicationId.toString())
        : of(null);

    const actions$ = forkJoin([sitePlanUpload$, gridAssessmentDocumentUpload$]).pipe(
      switchMap(([sitePlanDocumentsUploaded, gridAssessmentDocumentsUploaded]) => {
        const sitePlanDocumentId = sitePlanDocumentsUploaded && sitePlanDocumentsUploaded[0]?.DocumentId;
        const gridAssessmentDocumentId = gridAssessmentDocumentsUploaded && gridAssessmentDocumentsUploaded[0]?.DocumentId;

        if (sitePlanDocumentId) {
          data.sitePlan = {
            documentId: sitePlanDocumentId,
            contentType: sitePlan.content_type,
            filename: sitePlan.filename,
            name: sitePlan.name,
          };
        }

        if (gridAssessmentDocumentId) {
          data.gridAssessmentDocument = {
            documentId: gridAssessmentDocumentId,
            contentType: gridAssessmentDocument.content_type,
            filename: gridAssessmentDocument.filename,
            name: gridAssessmentDocument.name,
          };
        }

        return this.service.issuePermission(id, data);
      })
    );

    this.executeInternalUserAction(actions$);
  }

  requestExtension(id: number) {
    this.executeExternalUserAction(this.service.requestExtension(id));
  }

  updateRequest(id: number) {
    this.executeExternalUserAction(this.service.updateRequest(id));
  }

  withdrawRequest(id: number) {
    this.confirmationService.confirm({
      message: this.translate.translateString('grid-comp.order-process.withdraw-request-configmation-message'),
      accept: () => this.executeExternalUserAction(this.service.withdrawRequest(id)),
      reject: () => this.confirmationService.close(),
    });
  }

  withdrawAndCopyRequest(id: number) {
    this.confirmationService.confirm({
      message: this.translate.translateString('grid-comp.order-process.withdraw-and-copy-request-configmation-message'),
      accept: () => {
        this.isLoading = true;

        const gridCompFeedInApplication = this.gridCompatibilityService.gridApplication;
        this.preventLeak(
          this.service.withdrawAndCopyRequest(
            id,
            this.applicationId,
            gridCompFeedInApplication.sk_status,
            gridCompFeedInApplication.sk_type
          )
        ).subscribe(
          result => {
            this.setGridCompApplicationDataForUpdate(result);
            this.router.navigate(['/jurisdiction']);
          },
          error => this.errorService.throwErrorIfFailure(error, true)
        );
      },
      reject: () => this.confirmationService.close(),
    });
  }

  resolveIssue(id: number) {
    this.executeInternalUserAction(this.service.resolveIssue(id));
  }

  denyRequest(id: number) {
    this.executeInternalUserAction(this.service.denyRequest(id));
  }

  convertToDocument(gridCompDocument: GridCompatibilityResultDocument): Document {
    if (!gridCompDocument) {
      return new Document(GridApplicationDocumentTypes.Other);
    }

    return Object.assign(new Document(GridApplicationDocumentTypes.Other), {
      document_id: gridCompDocument.documentId.toString(),
      name: gridCompDocument.name,
      filename: gridCompDocument.filename,
      content_type: gridCompDocument.contentType,
      application_id: this.applicationId.toString(),
    });
  }

  changeFileData(documentAndInputName: DocumentAndInputName) {
    this.resultFormChange.next(this.resultForm.value);
    if (documentAndInputName?.inputName === 'gridAssessmentDocument') {
      this.isGridAssessmentDocumentUploaded = documentAndInputName?.document?.file ? true : false;
    } else if (documentAndInputName?.inputName === 'sitePlan') {
      this.isSitePlanDocumentUploaded = documentAndInputName?.document?.file ? true : false;
    }
  }

  canIssuePermission(form: FormGroup, orderProcess: GridCompOrderProcess) {
    if (form.invalid || !this.gridCompatibilityService.areAllSectionsReviewed()) {
      return false;
    }

    if (
      orderProcess.status === GridCompatibilityProcessStatus.FeedInPermissionIssued ||
      orderProcess.status === GridCompatibilityProcessStatus.FeedInPermissionExpired
    ) {
      return false;
    }

    if (form.value.feedInPermissionValidDate) {
      const today = new Date();
      return today < form.value.feedInPermissionValidDate;
    }

    return true;
  }

  public get showCopyApplication(): boolean {
    return (
      this.environmentService.showProceedToRegepButton &&
      this.canCopyApplicationData &&
      (this.userService.isUserTechnicianRole() || this.userService.isUserCustomerRole())
    );
  }

  public copyToRegepRequest() {
    const request = {
      source_application_id: this.applicationId,
      target_type: SkApplicationType.regep,
    };
    this.isLoading = true;
    this.preventLeak(this.copyAppDataService.copyApplicationData(request)).subscribe(
      copyAppId => {
        this.localStorageService.setItem('application_id', copyAppId.toString());
        this.localStorageService.setItem('installation_flow', InstallationFlowType.RegEP);
        this.router.navigate(['/jurisdiction']);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private executeExternalUserAction(actionObservable: Observable<any>) {
    this.preventLeak(actionObservable).subscribe(() => this.executedProcessAction.emit(GridApplicationProcessActionTypes.External));
  }

  private executeInternalUserAction(actionObservable: Observable<any>) {
    this.preventLeak(actionObservable).subscribe(() => this.executedProcessAction.emit(GridApplicationProcessActionTypes.Internal));
  }

  private buildResultForm(orderProcess: GridCompOrderProcess): FormGroup {
    const form = this.formBuilder.group({
      level: new FormControl(orderProcess.result?.level || null, Validators.required),
      transformation: new FormControl(orderProcess.result?.transformation || null, Validators.required),
      feedInPower: new FormControl(orderProcess.result?.feedInPower || null, Validators.required),
      powerDemand: new FormControl(orderProcess.result?.powerDemand || null, Validators.required),
      type: new FormControl(orderProcess.result?.type || null, Validators.required),
      isAccordingToVDEStandard: new FormControl(orderProcess.result?.isAccordingToVDEStandard, Validators.required),
      gridAssessmentDocument: new FormControl(this.convertToDocument(orderProcess.result?.gridAssessmentDocument)),
      sitePlan: new FormControl(this.convertToDocument(orderProcess.result?.sitePlan)),
      feedInPermissionValidDate: new FormControl(orderProcess.result?.feedInPermissionValidDate, [
        Validators.required,
        ValidationsService.feedInPermissionValidDateShouldBeInTheFutureValidator(),
      ]),
    });

    if (
      orderProcess.status === this.enums.orderProcessStatus.FeedInPermissionIssued ||
      orderProcess.status === this.enums.orderProcessStatus.FeedInPermissionExpired
    ) {
      form.disable();
    }

    if (orderProcess.result?.feedInPermissionValidDate && form.controls.feedInPermissionValidDate.invalid) {
      form.controls.feedInPermissionValidDate.markAsTouched();
    }

    this.preventLeak(form.valueChanges).subscribe(value => this.resultFormChange.emit(value));

    return form;
  }

  private setGridCompApplicationDataForUpdate(result: any) {
    this.localStorageService.setItem('update_application', 'true');
    this.localStorageService.setItem('gridcomp_update_application', 'true');
    this.localStorageService.setItem('installation_flow', 'gridCompatibility');
    this.localStorageService.setItem('application_id', result.applicationId.toString());
    this.localStorageService.setItem('connection_object_id', result.connectionobject.connectionobjectId.toString());
    this.localStorageService.setItem('business_partner_id', result.businesspartner.businesspartnerId.toString());
    this.localStorageService.setItem('area_locations', result.connectionobject.areaLocations);
    this.localStorageService.setItem('location_data', {
      city: result.connectionobject.city,
      postcode: result.connectionobject.postcode,
      street: result.connectionobject.street,
      street_number: result.connectionobject.streetNo,
    });
  }
}
